import { Divider, Group, Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import DateTimeInput from 'components/inputs/DateTimeInput';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { ERROR_NOTIFICATION_COLOR, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

export interface PasswordValidityModalProps {
  userId: number;
  fullName: string;
  passwordValidUntil?: string;
  opened: boolean;
  onClose: () => void;
  onPasswordValidityChange?: (passwordValidUntil: string) => void;
}

interface FormValues {
  passwordValidUntil?: string;
}

const INVALIDATED_PASSWORD_OFFSET_MS = 5 * 60 * 1000;

/**
 * Derives the initial modal form values from the current password-valid-until timestamp.
 */
function getInitialValues(passwordValidUntil?: string): FormValues {
  const date = passwordValidUntil ? new Date(passwordValidUntil) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;

  return {
    passwordValidUntil: safeDate.toISOString(),
  };
}

/**
 * Modal for setting a user's password validity to an exact date and time or invalidating it immediately.
 */
export default function PasswordValidityModal({
  userId,
  fullName,
  passwordValidUntil,
  opened,
  onClose,
  onPasswordValidityChange,
}: PasswordValidityModalProps) {
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const adminSetPasswordValidity = getAction('UserAdminSetPasswordValidity');

  const form = useForm<FormValues>({
    initialValues: getInitialValues(passwordValidUntil),
    validate: {
      passwordValidUntil: (value) => (value ? null : 'Pole je povinné.'),
    },
  });

  /**
   * Persists a precise password-valid-until timestamp for the selected user.
   */
  const savePasswordValidity = async (values: FormValues) => {
    startLoading();

    const passwordValidUntilIso = values.passwordValidUntil;

    if (!passwordValidUntilIso) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      stopLoading();
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    try {
      await adminSetPasswordValidity({
        parameters: { userId: String(userId) },
        payload: { passwordValidUntil: passwordValidUntilIso },
      });

      notifications.show({
        title: 'Platnosť hesla bola upravená',
        message:
          new Date(passwordValidUntilIso) <= new Date()
            ? 'Platnosť hesla už vypršala. Používateľ dostane e-mail s upozornením na expiráciu hesla.'
            : 'Nový termín platnosti hesla bol uložený.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      onPasswordValidityChange?.(passwordValidUntilIso);
      onClose();
    } catch (error: any) {
      if (error?.response?.data?.error?.code === 4000109) {
        notifications.show({
          title: 'Chyba!',
          message: 'Zadaný dátum alebo čas platnosti hesla nie je platný.',
          color: ERROR_NOTIFICATION_COLOR,
        });
        return;
      }

      panic(error);
    } finally {
      stopLoading();
    }
  };

  /**
   * Forces the selected user's password to become expired immediately.
   */
  const invalidateCompletely = async () => {
    startLoading();

    const invalidatedPasswordValidUntil = new Date(Date.now() - INVALIDATED_PASSWORD_OFFSET_MS).toISOString();

    try {
      await adminSetPasswordValidity({
        parameters: { userId: String(userId) },
        payload: { invalidateCompletely: true },
      });

      notifications.show({
        title: 'Heslo zneplatnené',
        message: 'Používateľ dostane e-mail s upozornením na expiráciu hesla.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      onPasswordValidityChange?.(invalidatedPasswordValidUntil);
      onClose();
    } catch (error) {
      panic(error instanceof Error ? error : new Error(String(error)));
    } finally {
      stopLoading();
    }
  };

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={`Platnosť hesla: ${fullName}`}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <Divider color="gray.1" mb={24} mt={12} />
      <form onSubmit={form.onSubmit(savePasswordValidity)}>
        <Stack spacing={24}>
          <P2Regular color="gray.7">
            Nastavte presný dátum a čas, dokedy zostane heslo platné. Ak chcete heslo zneplatniť okamžite, použite
            samostatné tlačidlo nižšie.
          </P2Regular>
          <DateTimeInput
            label="Platnosť hesla do"
            placeholder="Vyberte dátum a čas"
            size="lg"
            name="objekto-passwordValidUntil"
            {...form.getInputProps('passwordValidUntil')}
          />
          <Group position="apart" spacing={16} pt={8}>
            <Button variant="danger-secondary" onClick={invalidateCompletely} loading={loading}>
              Zneplatniť okamžite
            </Button>
            <Group spacing={16}>
              <Button variant="subtle-gray" onClick={onClose} px={8} disabled={loading}>
                Zrušiť
              </Button>
              <Button type="submit" variant="primary" loading={loading}>
                Uložiť
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
